/**
 * Vista previa al numerar los comprobantes de retención de una compra (pedido del usuario del 2026-10-30): en qué libro
 * de compras sale lo retenido (en la fila de la factura si el período es el de la compra; si no, una fila «Retención»
 * con la fecha de la retención) y en qué TXT del IVA (quincena) y XML del ISLR entra. La cuenta la hace
 * `vistaNumerarComprobantes` (`packages/shared/src/vista-libros.ts`), la misma que usa la prueba
 * `apps/api/test/bugs/vista-libros-numerar.spec.ts` para comprobar que el libro, el TXT y el XML dicen lo mismo.
 */
import { diaLegible, vistaNumerarComprobantes, type CompraDTO } from '@mg/shared';
import { AvisoLibro, BsLibro, FilaLibro, mesDelPeriodo, TarjetaVistaLibros, TituloLibro } from '@/componentes/VistaPreviaLibros.tsx';

type CompraVista = Pick<CompraDTO, 'periodo' | 'fecha_factura' | 'fecha_fiscal' | 'numero_factura' | 'proveedor_rif' | 'proveedor_razon_social' | 'comprobantes'> & { lineas: Pick<CompraDTO['lineas'][number], 'aplica_islr' | 'islr_concepto'>[] };

const AVISO = {
  periodo_invalido: () => 'Escribe el período como AAAAMM, con el mes de 01 a 12.',
  periodo_futuro: () => 'El período no puede ser un mes que todavía no llega.',
  periodo_anterior: (c: CompraVista) => `El período no puede ser anterior al de la compra (${mesDelPeriodo(c.periodo)}).`,
  sin_provisionales: () => 'No quedan comprobantes por numerar.',
};

export function VistaNumerar({ compra, periodo, ahora = new Date(), testid = 'numerar-vista-libros' }: { compra: CompraVista; periodo: string; ahora?: Date; testid?: string }) {
  const v = vistaNumerarComprobantes(compra, periodo, ahora);
  if (!v.ok) {
    return (
      <TarjetaVistaLibros testid={testid}>
        <AvisoLibro testid={`${testid}-aviso`}>{AVISO[v.motivo](compra)}</AvisoLibro>
      </TarjetaVistaLibros>
    );
  }
  const mes = mesDelPeriodo(v.periodo);
  // Si en ese período ya había un comprobante de IVA numerado (y ahora se numera otro de ISLR), el libro lleva ese número
  const ivaYa = compra.comprobantes.find((c) => c.tipo === 'iva' && c.estado === 'vigente' && c.numero && c.periodo === v.periodo)?.numero ?? null;
  const sinConcepto = v.provisionales.islr > 0 && compra.lineas.some((l) => l.aplica_islr && !l.islr_concepto);
  return (
    <TarjetaVistaLibros testid={testid}>
      <div className="space-y-0.5" data-testid={`${testid}-compras`}>
        <TituloLibro
          titulo={`Libro de compras de ${mes}`}
          testid={`${testid}-mes`}
          nota={v.misma_fila ? `Lo retenido entra en la fila de la factura ${compra.numero_factura}, que ya está en este libro, y se le quita la marca «retención provisional».` : `Sale una fila «Retención» nueva. La fila de la factura sigue en el libro de ${mesDelPeriodo(compra.periodo)}, con lo retenido en cero.`}
        />
        {v.misma_fila ? null : <FilaLibro etiqueta="Fecha" testid={`${testid}-fecha`}>{diaLegible(v.fecha_retencion)}</FilaLibro>}
        <FilaLibro etiqueta="RIF">{compra.proveedor_rif}</FilaLibro>
        <FilaLibro etiqueta="Proveedor">{compra.proveedor_razon_social}</FilaLibro>
        <FilaLibro etiqueta="N° factura">{compra.numero_factura}</FilaLibro>
        {v.misma_fila ? null : <FilaLibro etiqueta="Total, exento, base e IVA" testid={`${testid}-montos`}>en cero (van en el libro de la factura)</FilaLibro>}
        <FilaLibro etiqueta="IVA retenido" testid={`${testid}-iva`}><BsLibro valor={v.iva_bs} /></FilaLibro>
        <FilaLibro etiqueta="N° comprobante" testid={`${testid}-numero`}>{v.provisionales.iva > 0 ? `el siguiente de ${mes} (${v.periodo} y 8 cifras)` : ivaYa ?? '—'}</FilaLibro>
        <FilaLibro etiqueta="ISLR retenido" testid={`${testid}-islr`}><BsLibro valor={v.islr_bs} /></FilaLibro>
      </div>
      <div className="space-y-0.5 border-t pt-2" data-testid={`${testid}-declaracion`}>
        <TituloLibro titulo="Declaración" />
        {v.provisionales.iva > 0 ? <p data-testid={`${testid}-txt`}>TXT del IVA de {mes}, <b>quincena {v.quincena}</b> (fecha de la retención {diaLegible(v.fecha_retencion)}).</p> : null}
        {v.provisionales.islr > 0 ? <p data-testid={`${testid}-xml`}>XML del ISLR de {mes}, con fecha de operación {diaLegible(v.fecha_retencion)}.</p> : null}
        <p className="text-[11px] text-muted-foreground">Si ya bajaste el TXT o el XML de ese mes, la compra queda marcada para volver a bajarlos.</p>
        {sinConcepto ? <AvisoLibro testid={`${testid}-sin-concepto`}>Un renglón con ISLR no tiene concepto: el XML del ISLR de {mes} no se podrá bajar hasta ponérselo (con «Corregir»).</AvisoLibro> : null}
      </div>
    </TarjetaVistaLibros>
  );
}
